import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_KIND_MAP,
  NOTIFICATION_PRIORITY_MAP,
  type NotificationKind,
  type NotificationPriority,
} from '@goyone/shared';
import { api } from '../lib/api';
import { relative } from '../lib/format';
import { Icon } from './Icon';

export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  priority: NotificationPriority;
  link: string | null;
  entity: string | null;
  entityId: string | null;
  popup: boolean;
  readAt: string | null;
  createdAt: string;
}

interface NotificationPage {
  items: NotificationRow[];
  total: number;
  unread: number;
}

/**
 * كل كم يُسأل الخادم.
 *
 * دقيقة: التذكير بموعد بعد ربع ساعة لا يحتمل أكثر، والأقل من ذلك يضاعف
 * الطلبات بلا فائدة يشعر بها أحد. لا دفع فوري (WebSocket) بعد، والاستطلاع
 * كافٍ لما يقيسه النظام بالدقائق لا بالثواني.
 */
const POLL_MS = 60_000;

/* ==========================================================================
   الجرس
   ========================================================================== */

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api<NotificationPage>('/notifications?pageSize=12'),
    refetchInterval: POLL_MS,
    // يُعاد الجلب عند العودة إلى التبويب: من غاب ساعة يريد الجديد فورًا.
    refetchOnWindowFocus: true,
  });

  const markRead = useMutation({
    mutationFn: (ids: string[]) =>
      api('/notifications/read', { method: 'POST', body: { ids } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /* الإغلاق بالضغط خارج اللوحة أو بمفتاح الهروب. */
  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = list.data?.unread ?? 0;
  const items = list.data?.items ?? [];

  const openItem = (n: NotificationRow) => {
    if (!n.readAt) markRead.mutate([n.id]);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="notif-wrap" ref={panelRef}>
      <button
        className="topbar-gear notif-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `الإشعارات، ${unread} غير مقروء` : 'الإشعارات'}
        aria-expanded={open}
        title="الإشعارات"
      >
        <Icon name="bell" size={17} />
        {unread > 0 && <span className="notif-dot">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="الإشعارات">
          <div className="notif-head">
            <strong>الإشعارات</strong>
            <div className="row" style={{ gap: 6 }}>
              {unread > 0 && (
                <button className="btn btn-sm btn-ghost" onClick={() => markAll.mutate()}>
                  تعليم الكل مقروءًا
                </button>
              )}
              <Link
                to="/notifications"
                className="btn btn-sm btn-ghost"
                onClick={() => setOpen(false)}
              >
                الكل
              </Link>
            </div>
          </div>

          <div className="notif-list">
            {items.length === 0 && (
              <p className="notif-empty">لا إشعارات. يصلك هنا ما يستحق انتباهك.</p>
            )}

            {items.map((n) => (
              <div
                key={n.id}
                className={`notif-item${n.readAt ? '' : ' unread'}`}
                onClick={() => openItem(n)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openItem(n)}
              >
                <span className={`notif-mark kind-${NOTIFICATION_PRIORITY_MAP[n.priority].kind}`} />
                <div className="notif-body">
                  <div className="notif-title">{n.title}</div>
                  {n.body && <div className="notif-text">{n.body}</div>}
                  <div className="notif-meta">
                    {NOTIFICATION_KIND_MAP[n.kind]?.label ?? n.kind} · {relative(n.createdAt)}
                  </div>
                </div>
                <button
                  className="btn btn-icon btn-ghost btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss.mutate(n.id);
                  }}
                  aria-label="إخفاء"
                  title="إخفاء"
                >
                  <Icon name="x" size={13} />
                </button>
              </div>
            ))}
          </div>

          <Link to="/notifications" className="notif-foot" onClick={() => setOpen(false)}>
            عرض كل الإشعارات
          </Link>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   المنبثق
   ========================================================================== */

/**
 * الإشعار المنبثق.
 *
 * ما لا يحتمل انتظار أن يفتح المستخدم الجرس — موعد بعد دقائق، قرار في
 * إجازته، تغيّر في صلاحياته.
 *
 * والتعليم بأنه ظهر يقع بعد العرض لا قبله: لو عُلِّم عند الجلب لضاع
 * الإشعار كله إن أُغلق المتصفح في تلك اللحظة.
 */
/*
 * شاشاتٌ تملأ الإطار ويعمل فيها المستخدم بيديه.
 *
 * المحادثات ولوحة الاتصال وغرفة الحرب: المنبثق فيها يهبط فوق صندوق
 * الكتابة وزرّ الإرسال — يحجب ما تكتبه ويُنقر سهوًا بدله. فيُكتم فيها
 * ويبقى محسوبًا في الجرس بعدده وتفصيله، فلا معلومةَ تضيع؛ والعاجل
 * وحده يخترق الكتم لأنه لا يحتمل انتظار فتح الجرس.
 */
const IMMERSIVE_ROUTES = ['/crm/channels', '/callcenter', '/crm/war-room'];

export function NotificationPopups() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const immersive = IMMERSIVE_ROUTES.some((r) => pathname.startsWith(r));
  const [shown, setShown] = useState<NotificationRow[]>([]);
  const seen = useRef(new Set<string>());

  const pending = useQuery({
    queryKey: ['notifications', 'pending'],
    queryFn: () => api<NotificationRow[]>('/notifications/pending'),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });

  const markPopped = useMutation({
    mutationFn: (ids: string[]) =>
      api('/notifications/popped', { method: 'POST', body: { ids } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    const arrived = (pending.data ?? []).filter((n) => !seen.current.has(n.id));
    if (!arrived.length) return;

    /*
     * في الشاشات الممتلئة يُكتم غيرُ العاجل — ولا يُعلَّم «ظهر» ليبقى
     * في الجرس بلونه غير المقروء حتى يراه صاحبه هناك.
     */
    const incoming = immersive ? arrived.filter((n) => n.priority === 'URGENT') : arrived;
    if (!incoming.length) return;

    incoming.forEach((n) => seen.current.add(n.id));
    setShown((s) => [...incoming, ...s].slice(0, 3));
    markPopped.mutate(incoming.map((n) => n.id));

    /*
     * الاختفاء التلقائي للعادي وحده.
     *
     * العاجل يبقى حتى يُغلقه المستخدم: إشعار يخصّ الاشتراك أو الصلاحيات
     * يختفي بعد ثوانٍ كأنه لم يكن، وهو ما يجعل المستخدم يفاجأ لاحقًا بما
     * أُخبر به فعلًا.
     */
    for (const n of incoming) {
      if (n.priority === 'URGENT') continue;
      setTimeout(() => {
        setShown((s) => s.filter((x) => x.id !== n.id));
      }, 15_000);
    }
  }, [pending.data]);

  /* الدخولُ إلى شاشةٍ ممتلئة يطوي ما كان معلّقًا — إلا العاجل. */
  useEffect(() => {
    if (!immersive) return;
    setShown((s) => s.filter((n) => n.priority === 'URGENT'));
  }, [immersive]);

  const close = (id: string) => setShown((s) => s.filter((x) => x.id !== id));

  if (!shown.length) return null;

  return (
    <div className="popup-stack" role="region" aria-live="polite" aria-label="تنبيهات">
      {shown.map((n) => (
        <div key={n.id} className={`popup kind-${NOTIFICATION_PRIORITY_MAP[n.priority].kind}`}>
          <div className="popup-body">
            <div className="popup-title">{n.title}</div>
            {n.body && <div className="popup-text">{n.body}</div>}

            {n.link && (
              <button
                className="btn btn-sm btn-primary mt-2"
                onClick={() => {
                  close(n.id);
                  navigate(n.link!);
                }}
              >
                افتح
              </button>
            )}
          </div>

          <button
            className="btn btn-icon btn-ghost btn-sm"
            onClick={() => close(n.id)}
            aria-label="إغلاق التنبيه"
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
